import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import MobileNav from './components/MobileNav';
import Home from './pages/Home';
import Products from './pages/Products';
import Accessories from './pages/Accessories';
import Consumables from './pages/Consumables';
import ProductDetail from './pages/ProductDetail';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import sellerDashboard from './pages/seller/Dashboard';
import SellProduct from './pages/seller/SellProduct';
import MyProducts from './pages/seller/MyProducts';
import SellerProductDetail from './pages/seller/ProductDetail';
import EditProduct from './pages/seller/EditProduct';
import Messages from './pages/Messages';
import WantsChat from './pages/WantsChat';
import WantsList from './pages/WantsList';
import Talleres from './pages/Talleres';
import WantDetail from './pages/WantDetail';
import CreateWant from './pages/CreateWant';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminProducts from './pages/admin/Products';
import AdminReviews from './pages/admin/Reviews';
import AdminShops from './pages/admin/Shops';
import FeaturesManager from './pages/admin/FeaturesManager';
import HomepageEditor from './pages/admin/HomepageEditor';
import DownloadsManager from './pages/admin/Downloads';
import WishlistSidebar from './components/WishlistSidebar';
import ShopProfile from './pages/ShopProfile';
import Footer from './components/Footer';
import WeatherWidget from './components/WeatherWidget';
import Wishlist from './pages/Wishlist';
import Downloads from './pages/Downloads';
import HowToSell from './pages/HowToSell';
import SubscriptionPlans from './pages/SubscriptionPlans';
import SellerVerification from './pages/SellerVerification';
import Contact from './pages/Contact';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-500">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AppContent() {
  const location = useLocation();
  const isWantsChat = location.pathname.startsWith('/wants-chat');
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <WishlistSidebar />
      <main className={`flex flex-col flex-1 ${isWantsChat ? '' : 'pb-20 md:pb-0'}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/accessories" element={<Accessories />} />
          <Route path="/consumables" element={<Consumables />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
          <Route path="/seller/dashboard" element={<ProtectedRoute allowedRoles={['BUYER', 'SELLER', 'ADMIN']}><sellerDashboard /></ProtectedRoute>} />
          <Route path="/seller/products/new" element={<ProtectedRoute allowedRoles={['BUYER', 'SELLER', 'ADMIN']}><SellProduct /></ProtectedRoute>} />
          <Route path="/seller/products" element={<ProtectedRoute allowedRoles={['BUYER', 'SELLER', 'ADMIN']}><MyProducts /></ProtectedRoute>} />
          <Route path="/seller/products/:id" element={<ProtectedRoute allowedRoles={['BUYER', 'SELLER', 'ADMIN']}><SellerProductDetail /></ProtectedRoute>} />
          <Route path="/seller/products/:id/edit" element={<ProtectedRoute allowedRoles={['BUYER', 'SELLER', 'ADMIN']}><EditProduct /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/:userId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/wants" element={<WantsList />} />
          <Route path="/talleres" element={<Talleres />} />
          <Route path="/shops/:id" element={<ShopProfile />} />
          <Route path="/wants/:id" element={<WantDetail />} />
          <Route path="/wants/new" element={<ProtectedRoute><CreateWant /></ProtectedRoute>} />
          <Route path="/wants-chat" element={<WantsChat />} />
          <Route path="/wants-chat/:conversationId" element={<WantsChat />} />
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/how-to-sell" element={<HowToSell />} />
          <Route path="/plans" element={<SubscriptionPlans />} />
          <Route path="/verification" element={<SellerVerification />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/products" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminProducts /></ProtectedRoute>} />
          <Route path="/admin/reviews" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminReviews /></ProtectedRoute>} />
          <Route path="/admin/shops" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminShops /></ProtectedRoute>} />
          <Route path="/admin/downloads" element={<ProtectedRoute allowedRoles={['ADMIN']}><DownloadsManager /></ProtectedRoute>} />
        </Routes>
      </main>
      <MobileNav />
      {!isWantsChat && <Footer />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
